import { useRouter } from "@tanstack/react-router"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "design-system/molecules/Tabs"
import { cn } from "design-system/utils"
import { ensure } from "lopermut"
import { FileText, Globe, MessagesSquare, Text, type LucideIcon } from "lucide-react"
import { forwardRef, useImperativeHandle, useRef } from "react"
import { useTranslation } from "react-i18next"
import { KnowledgeBaseItemKind } from "schema"
import { KnowledgeBaseItem, KnowledgeBaseItemRef } from "./KnowledgeBaseItem"

type Props = {
  campaignId?: string
  wrapperClassName?: string
  highlightId?: string
  section?: KnowledgeBaseItemKind
}

export type KnowledgeBaseItemsRef = {
  addItemToActiveTab: () => void
}

export const KnowledgeBaseItems = forwardRef<KnowledgeBaseItemsRef, Props>(
  (
    {
      campaignId,
      wrapperClassName,
      section = KnowledgeBaseItemKind.QuestionAndAnswer,
      highlightId,
    },
    ref,
  ) => {
    const { t } = useTranslation()
    const { navigate } = useRouter()

    const tabs: { name: string; value: KnowledgeBaseItemKind; icon: LucideIcon }[] = [
      {
        name: t("knowledge.knowledgeBase.tabs.questionAndAnswer"),
        value: "QuestionAndAnswer",
        icon: MessagesSquare,
      },
      {
        name: t("knowledge.knowledgeBase.tabs.text"),
        value: "Text",
        icon: Text,
      },
      {
        name: t("knowledge.knowledgeBase.tabs.file"),
        value: "File",
        icon: FileText,
      },
      {
        name: t("knowledge.knowledgeBase.tabs.website"),
        value: "Website",
        icon: Globe,
      },
    ]

    const knowledgeBaseRefs = useRef<Record<string, KnowledgeBaseItemRef | null>>({})
    const activeTabRef = useRef<string>(section ?? ensure(tabs[0]).value)

    useImperativeHandle(
      ref,
      () => ({
        addItemToActiveTab: () => {
          const activeRef = knowledgeBaseRefs.current[activeTabRef.current]
          activeRef?.addItem()
        },
      }),
      [],
    )

    return (
      <Tabs
        variant="pill"
        orientation="vertical"
        defaultValue={activeTabRef.current}
        onValueChange={(value) => {
          activeTabRef.current = value
          navigate({ to: ".", search: { section: value as KnowledgeBaseItemKind } })
        }}
        className={cn(
          "flex lg:flex-row flex-col items-start gap-6 justify-center",
          wrapperClassName,
        )}
      >
        <TabsList className="max-w-full sticky top-20 lg:grid lg:grid-cols-1 gap-1 p-0 bg-background shrink-0 flex flex-row items-start justify-start overflow-x-auto overflow-y-hidden md:overflow-visible z-20">
          {tabs.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              className={cn(
                "min-w-fit lg:min-w-48 data-[state=active]:bg-primary",
                "data-[state=active]:text-primary-foreground px-3 py-1.5 flex",
                "items-center justify-between gap-2.5",
              )}
            >
              <div className="flex items-center gap-2.5">
                <tab.icon className="h-5 w-5" />
                <span>{tab.name}</span>
              </div>
            </TabsTrigger>
          ))}
        </TabsList>
        <div className="h-auto w-full">
          {tabs.map((tab) => (
            <TabsContent key={tab.value} value={tab.value} className="h-full w-full">
              <KnowledgeBaseItem
                ref={(el) => {
                  knowledgeBaseRefs.current[tab.value] = el
                }}
                kind={tab.value}
                campaignId={campaignId}
                highlightId={highlightId}
              />
            </TabsContent>
          ))}
        </div>
      </Tabs>
    )
  },
)

KnowledgeBaseItems.displayName = "KnowledgeBaseItems"
